import React, { FC, useRef, useState } from 'react';
import { ScrollView, Text, View } from 'react-native';
import { bottomBarHeight, fullHeight, scale, webHeader } from '@components/frame';
import ColorWrapper from '@styles/colors';
import { getBoxShadowStyle } from '@styles/shadow';
import { ItemName } from '@v2/pages/batch/details/modules/batch/components/BatchDetailsBlock/BatchDetailsBlockContent';
import i18n from 'i18n-js';
import { font } from '@styles/fonts';
import CloseButton from '@components/closeButton';
import EventStatusSubStatusExecMode, { EventStatusSubStatusExecModeProps } from '@v2/pages/common/components/NCFileBatchEvents/components/EventStatusSubStatusExecMode';
import InputLine from '@components/machines/components/detailsComponents/activeMachine/components/InputLine';
import { useManageTraceEventPopup } from './hooks/useManageTraceEventPopup';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import { TRACE_EVENT_POPUP_CONFIG } from './constants';
import { isPhoneNative, isWeb } from '@helpers';
import { RawMachineEventDataDisplay } from '@v2/pages/common/components/NCFileBatchEvents/components/RawMachineEventDataDisplay';
import BlockWrapper from '@components/common/BlockWrapper';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

export interface TraceEventPopupProps extends Pick<EventStatusSubStatusExecModeProps, 'C' | 'event'> {
  onClose: () => void;
}

const TraceEventPopup: FC<TraceEventPopupProps> = ({ C, event, onClose }) => {
  const shadowStyles = getBoxShadowStyle({ type: 'Very Wide', C });

  const { config, eventVersion, ncFileName, onNcFileClick, lineBg, parsedRawMachineEventData } = useManageTraceEventPopup({ C, event, onClose });

  const isMobileDevice = isPhoneNative;

  // Refs for scroll tracking
  const innerScrollRef = useRef<ScrollView>(null);
  const innerScrollOffset = useRef(0);
  const prevInnerScrollOffset = useRef(0);

  // Track outer ScrollView layout height for inner container maxHeight calculation
  const [outerScrollLayoutHeight, setOuterScrollLayoutHeight] = useState(0);

  // Track if outer scroll is at bottom to enable inner scroll
  const [isOuterScrollAtBottom, setIsOuterScrollAtBottom] = useState(false);

  const fixedHeaderHeight =
    scale(TRACE_EVENT_POPUP_CONFIG.headerMarginBottom) +
    scale(TRACE_EVENT_POPUP_CONFIG.headerRowHeight) +
    scale(TRACE_EVENT_POPUP_CONFIG.statusRowHeight);

  const verticalPopupPadding = scale(TRACE_EVENT_POPUP_CONFIG.popupPaddingTop) + scale(TRACE_EVENT_POPUP_CONFIG.popupPaddingBottom);
  const panelHeight = fullHeight - (isWeb ? webHeader : 0);

  const nativePopupPadding = scale(TRACE_EVENT_POPUP_CONFIG.popupPaddingMobile) * 2;
  const bottomMargin = scale(TRACE_EVENT_POPUP_CONFIG.bottomMargin);
  const maxPopupHeight = fullHeight - nativePopupPadding - bottomMargin;

  const availableScrollHeight = isMobileDevice ? maxPopupHeight - fixedHeaderHeight : panelHeight - verticalPopupPadding - fixedHeaderHeight;

  return (
    <View
      testID={TEST_IDS.ncFileBatchEvents.traceEventPopup}
      style={[
        !isMobileDevice && shadowStyles,
        !isMobileDevice && { backgroundColor: C('Background/Light/Modal Highlight') },
        isMobileDevice
          ? {
              padding: scale(TRACE_EVENT_POPUP_CONFIG.popupPaddingMobile),
              maxHeight: maxPopupHeight,
            }
          : {
              paddingTop: scale(TRACE_EVENT_POPUP_CONFIG.popupPaddingTop),
              paddingBottom: scale(TRACE_EVENT_POPUP_CONFIG.popupPaddingBottom),
              paddingHorizontal: scale(TRACE_EVENT_POPUP_CONFIG.popupPaddingHorizontal),
              height: panelHeight,
            },
      ]}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: scale(TRACE_EVENT_POPUP_CONFIG.headerMarginBottom) }}>
        <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', minWidth: 0, gap: scale(9) }}>
          <ItemName {...{ C, onPress: onNcFileClick, text: ncFileName, containerStyle: { flexShrink: 1, maxWidth: '80%' } }} />
          <Text numberOfLines={1} style={[font['Subhead 2'], { color: C('Label/Light/Tertiary'), flexShrink: 0 }]}>
            {i18n.t('Version')} {eventVersion}
          </Text>
        </View>
        <View style={{ flexShrink: 0, marginLeft: scale(9) }}>
          <CloseButton C={C} onPress={onClose} takeIconForPhone />
        </View>
      </View>

      <View>
        <EventStatusSubStatusExecMode C={C} event={event} displayStatusAsBadge withoutChevron />
      </View>
      <ScrollView
        style={{ maxHeight: availableScrollHeight }}
        showsVerticalScrollIndicator={false}
        nestedScrollEnabled
        scrollEventThrottle={TRACE_EVENT_POPUP_CONFIG.scrollEventThrottle}
        onLayout={(e) => setOuterScrollLayoutHeight(e.nativeEvent.layout.height)}
        onScroll={(e) => {
          const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
          const isAtBottom = contentOffset.y + layoutMeasurement.height >= contentSize.height - TRACE_EVENT_POPUP_CONFIG.scrollBottomThreshold;
          if (isAtBottom !== isOuterScrollAtBottom) {
            setIsOuterScrollAtBottom(isAtBottom);
          }
        }}
      >
        <BlockWrapper title="TRACE EVENT" withoutShadow titleStyles={{ marginTop: scale(TRACE_EVENT_POPUP_CONFIG.gapBetweenBlocks) }}>
          {config.map((item, index) => (
            <InputLine
              {...{
                bg: lineBg,
                ...item,
                index,
                data: config,
                C,
                withoutStretchingForContainer: true,
                layoutForPhone: true,
              }}
              key={item.i.name}
            />
          ))}
        </BlockWrapper>
        <BlockWrapper title="DETAIL INFORMATION" withoutShadow titleStyles={{ marginTop: scale(TRACE_EVENT_POPUP_CONFIG.gapBetweenBlocks) }}>
          <View
            style={{
              backgroundColor: C(lineBg),
              paddingHorizontal: scale(TRACE_EVENT_POPUP_CONFIG.detailInfoPaddingHorizontal),
              paddingVertical: scale(TRACE_EVENT_POPUP_CONFIG.detailInfoPaddingVertical),
              borderRadius: scale(SCREEN_BORDER_RADIUS),
              maxHeight: outerScrollLayoutHeight > 0 ? outerScrollLayoutHeight - scale(TRACE_EVENT_POPUP_CONFIG.gapBetweenBlocks) * 2 : undefined,
            }}
          >
            <ScrollView
              ref={innerScrollRef}
              showsVerticalScrollIndicator={false}
              nestedScrollEnabled
              scrollEnabled={isOuterScrollAtBottom}
              bounces={false}
              scrollEventThrottle={TRACE_EVENT_POPUP_CONFIG.scrollEventThrottle}
              onScroll={(e) => {
                const currentOffset = e.nativeEvent.contentOffset.y;
                const wasAlreadyAtTop = prevInnerScrollOffset.current <= 0;
                const isAtTop = currentOffset <= 0;
                const isScrollingUp = currentOffset < prevInnerScrollOffset.current;

                // Mirror behavior: only switch to outer if we were ALREADY at top and user continues scrolling up
                // This prevents triggering during the scroll that brings us to top
                if (wasAlreadyAtTop && isAtTop && isScrollingUp && isOuterScrollAtBottom) {
                  setIsOuterScrollAtBottom(false);
                }

                prevInnerScrollOffset.current = currentOffset;
                innerScrollOffset.current = currentOffset;
              }}
            >
              {parsedRawMachineEventData && <RawMachineEventDataDisplay data={parsedRawMachineEventData} />}
            </ScrollView>
          </View>
        </BlockWrapper>
      </ScrollView>
    </View>
  );
};

export default ColorWrapper(TraceEventPopup);
